'use client';

import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import NextImage from 'next/image';
import { gsap } from 'gsap';
import { useGuideVerticalProgress } from './useGuideVerticalProgress';
import { ChevronDown } from 'lucide-react';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/components/ui/utils';
import { getImageUrlForVariant } from '@/lib/homepage-mappers';
import { getStrapiOriginalMediaUrl } from '@/lib/strapi-media-url';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { HomeBuildingGuideItem } from '../../data/guides';
import type { TransformedGuideInnerData } from '@/types/guide-inner';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

type NullableString = string | null | undefined;

interface BudgetGuidePageProps {
  locale: string;
  guide: HomeBuildingGuideItem;
  cmsData?: TransformedGuideInnerData | null;
  /**
   * Streaming slot for the FAQ + enquiry block — rendered by an async
   * Server Component wrapped in `<Suspense>` in `page.tsx`.
   */
  faqSlot?: React.ReactNode;
}

type BudgetAccordionItem = {
  id: string;
  title: string;
  description: string;
};

type BudgetBlock = {
  title: string;
  description: string;
  accordionItems: BudgetAccordionItem[];
  image: string;
};

const AUTO_ADVANCE_MS = 8000;

const HARDCODED_BUDGET_BLOCKS: BudgetBlock[] = [
  {
    title: 'The 60/40 Rule',
    description:
      'In India, a balanced budget typically follows a 60/40 split. Understanding this construction cost breakdown prioritizes the core investments in the initial framework and running out of funds before the house is livable.',
    accordionItems: [
      {
        id: 'structure',
        title: 'The Structure (60%)',
        description:
          'This covers the foundation, RCC, brickwork, and plastering. This is your investment in strength, never compromise quality here.',
      },
      {
        id: 'finishes',
        title: 'The Finishes (40%)',
        description:
          'This includes tiles, paint, fittings, and décor. Keep choices practical to maintain overall budget discipline.',
      },
    ],
    image: '/images/guid_342.png',
  },
  {
    title: 'Account for the Invisible Costs',
    description: "Homeowners often miss the expenses that don't appear in a contractor's quote.",
    accordionItems: [
      {
        id: 'statutory',
        title: 'Statutory Fees',
        description:
          'Government approvals, map sanctions, and utility connections (water/power) can add 3-5% to your total outlay.',
      },
      {
        id: 'professional',
        title: 'Professional Fees',
        description:
          'Architect, structural consultant, and supervision fees should be allocated at the planning stage itself.',
      },
    ],
    image: '/images/guid_343.png',
  },
  {
    title: 'The 10% Contingency Buffer',
    description:
      'Price hikes in steel and cement are common. When planning the house construction budget, it is ideal to set aside a 10% safety net that remains untouched until the final phase.',
    accordionItems: [],
    image: '/images/guid_344.png',
  },
  {
    title: 'Material Procurement Strategy',
    description:
      'Material cost for house construction accounts for nearly 60% of your total expenditure. Purchasing cement and steel in bulk can save significant costs, but only if you have moisture-proof storage plans.',
    accordionItems: [],
    image: '/images/guid_345.png',
  },
];

function stripHtml(html: NullableString): string {
  if (!html) return '';
  return html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&apos;/gi, "'")
    .replace(/&rsquo;/gi, '’')
    .trim();
}

function buildBudgetBlocksFromCms(
  cmsData: TransformedGuideInnerData | null | undefined,
): BudgetBlock[] {
  return (cmsData?.accordionBlocks ?? [])
    .filter((b) => !b.otherItemsBlock)
    .map((b) => ({
      title: b.heading ?? '',
      description: stripHtml(b.description),
      accordionItems: (b.accordionItem ?? []).map((item, idx) => ({
        id: `cms-${b.id}-${item.id ?? idx}`,
        title: item.heading,
        description: stripHtml(item.content),
      })),
      image: getImageUrlForVariant(b.image, 'large') ?? '/images/guid_342.png',
    }));
}

function BudgetAccordion({ items }: { items: BudgetAccordionItem[] }) {
  const [activeId, setActiveId] = React.useState(items[0]?.id ?? '');
  const progressRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const progressTween = React.useRef<gsap.core.Tween | null>(null);

  const goToNext = useCallback(() => {
    const currentIndex = items.findIndex((x) => x.id === activeId);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % items.length : 0;
    const nextId = items[nextIndex]?.id;
    if (nextId) setActiveId(nextId);
  }, [activeId, items]);

  useEffect(() => {
    for (const item of items) {
      const isActive = item.id === activeId;
      const progressEl = progressRefs.current[item.id];
      if (progressEl && !isActive) {
        gsap.killTweensOf(progressEl);
        gsap.set(progressEl, { width: '0%' });
      }
    }

    const activeProgressEl = progressRefs.current[activeId];
    if (activeProgressEl) {
      progressTween.current?.kill();
      gsap.set(activeProgressEl, { width: '0%' });
      progressTween.current = gsap.to(activeProgressEl, {
        width: '100%',
        duration: AUTO_ADVANCE_MS / 1000,
        ease: 'none',
        onComplete: goToNext,
      });
    }

    return () => {
      progressTween.current?.kill();
    };
  }, [activeId, goToNext, items]);

  return (
    <div className="divide-y divide-[#D8D8D8] border-y border-[#D8D8D8]">
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <div key={item.id} className="relative">
            <div className="pointer-events-none absolute left-0 right-0 top-0 h-[2px] overflow-hidden bg-[#ECECEC]">
              <span
                ref={(el) => {
                  progressRefs.current[item.id] = el;
                }}
                className="absolute inset-y-0 left-0 w-0 bg-[#E31E24]"
              />
            </div>

            <button
              type="button"
              onClick={() => setActiveId(item.id)}
              className={cn(
                'flex w-full items-center justify-between gap-4 px-0 py-4 text-left transition-colors',
                isActive ? 'text-[#151515]' : 'text-[#242424]',
              )}
            >
              <span className="text-base font-medium leading-tight sm:text-[18px]">
                {item.title}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-[#2A2A2A] transition-transform duration-300',
                  isActive && 'rotate-180',
                )}
              />
            </button>

            <div className={cn(!isActive && 'hidden')}>
              <p className="pb-4 pr-6 text-[12px] leading-[1.55] text-[#515151]">
                {item.description}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const DEFAULT_CHECKLIST_ITEMS = [
  {
    id: 1,
    heading: 'Finalise Built-up Area',
    description: 'Confirm the exact square footage before getting quotes.',
  },
  {
    id: 2,
    heading: 'Secure Approvals',
    description: 'Account for all local municipal fees and permits.',
  },
  {
    id: 3,
    heading: 'Set the Buffer',
    description: 'Allocate a contingency fund for unexpected price hikes.',
  },
  {
    id: 4,
    heading: 'Define Finish Levels',
    description: 'Decide on tile, fixture, and paint grades early.',
  },
  {
    id: 5,
    heading: 'Milestone Payments',
    description: 'Structure your contract so payments are tied to physical work completed.',
  },
];

function BudgetChecklistSection({
  checklist,
  hidden,
}: {
  checklist: TransformedGuideInnerData['checklist'] | undefined;
  hidden: boolean;
}) {
  const items = checklist?.data ?? DEFAULT_CHECKLIST_ITEMS;
  return (
    <section className={`${hidden ? 'hidden' : ''} bg-[#efefef] py-12 md:py-16`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_1fr] lg:gap-14">
          <div className="max-w-[560px]">
            <h3 className="text-3xl font-semibold leading-[1.08] text-[#111827] sm:text-4xl lg:text-6xl">
              {checklist?.heading ?? 'Home Construction Budget Planning Checklist'}
            </h3>
            {checklist?.subHeading ? (
              <div
                className="mt-4 max-w-md text-sm leading-relaxed text-[#4b5563] md:text-base [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: checklist.subHeading }}
              />
            ) : (
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#4b5563] md:text-base">
                To effectively plan a budget for the construction of your house, keep this checklist
                handy:
              </p>
            )}
          </div>

          <div className="rounded-xl bg-white px-5 py-5 sm:px-6 sm:py-6 lg:px-7 lg:py-7">
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.id} className="flex items-start gap-3">
                  <NextImage
                    src="/images/checkgrn.svg"
                    alt=""
                    aria-hidden
                    width={16}
                    height={16}
                    className="mt-1 shrink-0"
                  />
                  <div>
                    <p className="text-sm font-semibold leading-tight text-[#111827] md:text-base">
                      {item.heading}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-[#6b7280] md:text-sm">
                      {stripHtml(item.description)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function BudgetToolkitSection({
  toolkit,
  toolkitButtons,
  hidden,
  locale,
  resolveButtonHref,
}: {
  toolkit: TransformedGuideInnerData['toolkit'] | undefined;
  toolkitButtons: NonNullable<TransformedGuideInnerData['toolkit']>['buttonWhite'];
  hidden: boolean;
  locale: string;
  resolveButtonHref: (buttonUrl: NullableString, fallback: string) => string;
}) {
  return (
    <section className={`${hidden ? 'hidden' : ''} bg-[#efefef] pb-12 md:pb-16`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="relative overflow-hidden rounded-2xl bg-[#3a0004] px-5 py-6 sm:px-6 lg:px-10 max-sm:!pb-0">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_0.95fr]">
            <div className="relative z-10 max-w-[520px] text-white lg:py-14">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                {toolkit?.heading ?? 'Your Precision Toolkit'}
              </p>
              {toolkit?.subHeading ? (
                <div
                  className="mt-3 max-w-sm text-xl font-semibold leading-tight sm:text-2xl [&_p]:m-0"
                  dangerouslySetInnerHTML={{ __html: toolkit.subHeading }}
                />
              ) : (
                <h3 className="mt-3 max-w-sm text-xl font-semibold leading-tight sm:text-2xl">
                  Planning the next step of your project? We&apos;re here to help.
                </h3>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                {toolkitButtons.length > 0 ? (
                  toolkitButtons.map((btn, idx) => {
                    const isOutlined = btn.buttonType === 'Transparent';
                    const href = resolveButtonHref(btn.buttonUrl, `/${locale}/tools`);
                    return (
                      <SwapTextButton
                        key={btn.id ?? idx}
                        text={btn.buttonText}
                        hoverText={btn.buttonText}
                        href={href}
                        {...getCmsButtonLinkProps(btn)}
                        tone={isOutlined ? undefined : 'light'}
                        variant={isOutlined ? 'outline' : undefined}
                        showArrow={false}
                        className={cn(
                          'min-h-[40px] w-full justify-center rounded-md px-4 py-2 text-xs font-semibold shadow-none sm:w-auto',
                          isOutlined &&
                            '!border-white !bg-transparent !text-white hover:!bg-white hover:!text-[#111827]',
                        )}
                        aria-label={btn.ariaLabel ?? btn.buttonText}
                      />
                    );
                  })
                ) : (
                  <>
                    <SwapTextButton
                      text="Find a Dealer Near You"
                      hoverText="Find a Dealer Near You"
                      href={`/${locale}/tools/dealer-locator`}
                      tone="light"
                      showArrow={false}
                      className="min-h-[40px] w-full justify-center rounded-md px-4 py-2 text-xs font-semibold shadow-none sm:w-auto"
                      aria-label="Find a dealer near you"
                    />
                    <SwapTextButton
                      text="Estimate Construction Cost"
                      hoverText="Estimate Construction Cost"
                      href={`/${locale}/tools/construction-cost-calculator`}
                      variant="outline"
                      showArrow={false}
                      className="min-h-[40px] w-full justify-center rounded-md !border-white !bg-transparent px-4 py-2 text-xs font-semibold !text-white shadow-none hover:!bg-white hover:!text-[#111827] sm:w-auto"
                      aria-label="Estimate construction cost"
                    />
                  </>
                )}
              </div>
            </div>

            <div className="relative min-h-[220px] sm:min-h-[260px] lg:min-h-[320px]">
              <NextImage
                src={
                  getImageUrlForVariant(toolkit?.image ?? null, 'large') ?? '/images/guid_350.png'
                }
                alt={toolkit?.heading ?? 'Planning support toolkit'}
                fill
                sizes="(max-width: 768px) 100vw, 45vw"
                className="object-cover object-right"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BudgetGuideHero({
  heroImage,
  heroMedia,
  heroAlt,
  heroWidth,
  heroHeight,
  heroTitle,
  heroTitleRichText,
  heroSubtitle,
  heroDescription,
}: {
  heroImage: string;
  heroMedia: TransformedGuideInnerData['hero']['bannerMedia'] | null;
  heroAlt: string;
  heroWidth: number;
  heroHeight: number;
  heroTitle: string;
  heroTitleRichText: string | null;
  heroSubtitle: string;
  heroDescription: string;
}) {
  return (
    <section className="overflow-hidden bg-black font-figtree">
      <div className="lg:hidden">
        <CmsImage
          src={heroImage}
          media={heroMedia}
          alt={heroAlt}
          width={heroWidth}
          height={heroHeight}
          priority
          loading="eager"
          sizes="100vw"
          className="h-auto w-full"
          posterSrc={MEDIA_PLACEHOLDER}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />

        <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-10 md:py-12">
          <div className="grid gap-6">
            <div className="max-w-[620px]">
              <CmsRichTextHeading
                className="hero-title text-white"
                html={heroTitleRichText}
                fallback={heroTitle}
              />
            </div>

            <div className="max-w-[420px]">
              <h2 className="text-2xl font-medium leading-tight text-white sm:text-3xl md:text-[2rem]">
                {heroSubtitle}
              </h2>
              <p className="hero-subtitle mt-4 text-white/85">{heroDescription}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="relative hidden h-[100svh] overflow-hidden bg-black lg:block">
        <CmsImage
          src={heroImage}
          media={heroMedia}
          alt={heroAlt}
          fill
          priority
          loading="eager"
          sizes="100vw"
          className="object-cover object-center"
          posterSrc={MEDIA_PLACEHOLDER}
          fallbackSrc={MEDIA_PLACEHOLDER}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/35 to-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/10" />

        <div className="relative z-10 h-full">
          <div className="h-full container mx-auto">
            <div className="grid h-full w-full grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(240px,260px)] xl:grid-cols-[minmax(0,1fr)_minmax(300px,450px)]">
              <div className="max-w-[430px] self-start pt-[148px] xl:max-w-[640px] xl:pt-[168px]">
                <CmsRichTextHeading
                  className="hero-title text-balance text-white"
                  html={heroTitleRichText}
                  fallback={heroTitle}
                />
              </div>

              <div className="max-w-[370px] self-end pb-[72px] lg:justify-self-end xl:max-w-[400px]">
                <h2 className="text-[18px] font-semibold leading-[1.35] tracking-[-0.015em] text-white xl:text-[24px]">
                  {heroSubtitle}
                </h2>
                <p className="mt-2 text-[14px] leading-[1.55] tracking-[-0.01em] text-white/85 xl:mt-3 xl:text-[16px]">
                  {heroDescription}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Resolve the hero block props (CMS values with hardcoded fallbacks). */
function resolveBudgetHero(
  cmsData: TransformedGuideInnerData | null | undefined,
  guide: HomeBuildingGuideItem,
) {
  const heroMedia = cmsData?.hero.bannerMedia ?? null;
  return {
    heroTitle: cmsData?.hero.heading || 'Planning the Construction Budget for Your Dream Home',
    heroTitleRichText: cmsData?.hero.titleRichText ?? null,
    heroSubtitle:
      stripHtml(cmsData?.hero.subheadingHtml) ||
      'Financial clarity is the foundation of a successful build.',
    heroDescription:
      stripHtml(cmsData?.hero.descriptionHtml) ||
      "Planning a home budget is about more than just totaling costs. It's about strategic allocation, ensuring you have the resources to maintain quality from the foundation to the final coat of paint. By defining your limits early, you turn a complex construction journey into a predictable, manageable plan.",
    heroMedia,
    heroImage: getStrapiOriginalMediaUrl(heroMedia) ?? '/images/guid_12.png',
    heroAlt: heroMedia?.alternativeText || guide.title,
    heroWidth: heroMedia?.width || 1440,
    heroHeight: heroMedia?.height || 810,
  };
}

export default function BudgetGuidePage({ locale, guide, cmsData, faqSlot }: BudgetGuidePageProps) {
  const budgetFlowSectionRef = useRef<HTMLElement | null>(null);
  const budgetFlowContentRef = useRef<HTMLDivElement | null>(null);
  const progressColumnRef = useRef<HTMLDivElement | null>(null);
  const progressPinRef = useRef<HTMLDivElement | null>(null);
  const progressRailRef = useRef<HTMLDivElement | null>(null);
  const progressFillRef = useRef<HTMLDivElement | null>(null);
  const progressDotRef = useRef<HTMLSpanElement | null>(null);

  const {
    heroTitle,
    heroTitleRichText,
    heroSubtitle,
    heroDescription,
    heroMedia,
    heroImage,
    heroAlt,
    heroWidth,
    heroHeight,
  } = resolveBudgetHero(cmsData, guide);

  const ctaCard = cmsData?.textWithImage;
  const ctaButton = ctaCard?.buttonDark?.[0] ?? ctaCard?.buttonWhite?.[0] ?? null;

  const cmsBlocks: BudgetBlock[] = buildBudgetBlocksFromCms(cmsData);
  const budgetBlocks = cmsBlocks.length > 0 ? cmsBlocks : HARDCODED_BUDGET_BLOCKS;

  const checklist = cmsData?.checklist;
  const toolkit = cmsData?.toolkit;
  const toolkitButtons = [...(toolkit?.buttonWhite ?? []), ...(toolkit?.buttonDark ?? [])];

  const resolveButtonHref = (buttonUrl: NullableString, fallback: string) => {
    if (!buttonUrl) return fallback;
    return buttonUrl.startsWith('http') ? buttonUrl : `/${locale}${buttonUrl}`;
  };

  const ctaHref = resolveButtonHref(
    ctaButton?.buttonUrl,
    `/${locale}/tools/construction-cost-calculator`,
  );

  useGuideVerticalProgress(
    {
      sectionRef: budgetFlowSectionRef,
      contentRef: budgetFlowContentRef,
      progressColumnRef,
      progressPinRef,
      progressRailRef,
      progressFillRef,
      progressDotRef,
    },
    budgetBlocks.length,
  );

  return (
    <>
      <BudgetGuideHero
        heroImage={heroImage}
        heroMedia={heroMedia}
        heroAlt={heroAlt}
        heroWidth={heroWidth}
        heroHeight={heroHeight}
        heroTitle={heroTitle}
        heroTitleRichText={heroTitleRichText}
        heroSubtitle={heroSubtitle}
        heroDescription={heroDescription}
      />

      <section className={`${cmsData && !ctaCard ? 'hidden' : ''} bg-black py-12 md:py-16`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-0">
          <div className="overflow-hidden rounded-2xl bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.6fr]">
              <div className="px-5 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
                <h3 className="text-2xl font-semibold leading-tight text-[#111827] sm:text-3xl lg:text-4xl">
                  {ctaCard?.heading || 'The Fastest Way To Set Your Baseline Budget'}
                </h3>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#4b5563] md:text-base">
                  {stripHtml(ctaCard?.description) ||
                    'Before you dive into the details, use our Construction Cost Calculator to get a localized, real-time house building cost estimate based on your city and plot size.'}
                </p>
                <SwapTextButton
                  text={ctaButton?.buttonText ?? 'Get an Instant Estimate'}
                  hoverText={ctaButton?.buttonText ?? 'Get an Instant Estimate'}
                  href={ctaHref}
                  showArrow={false}
                  className="mt-6 w-full justify-center rounded-md px-5 py-2.5 text-sm font-semibold sm:w-auto"
                  aria-label={ctaButton?.ariaLabel ?? 'Get an instant estimate'}
                />
              </div>

              <div className="relative min-h-[220px] sm:min-h-[260px] lg:min-h-full">
                <NextImage
                  src={
                    getImageUrlForVariant(ctaCard?.image ?? null, 'large') ?? '/images/guid_341.png'
                  }
                  alt={ctaCard?.heading ?? 'Construction budget calculator preview'}
                  fill
                  sizes="(max-width: 768px) 100vw, 40vw"
                  className="object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={budgetFlowSectionRef}
        className={`${cmsData && cmsData.accordionBlocks.length === 0 ? 'hidden' : ''} bg-black py-10 md:py-14`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-0">
          <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[26px_1fr] lg:gap-8">
            <div ref={progressColumnRef} className="relative hidden w-[26px] shrink-0 lg:block">
              <div ref={progressPinRef} className="relative h-full w-full">
                <div
                  ref={progressRailRef}
                  className="absolute bottom-0 left-1/2 top-0 w-px -translate-x-1/2 bg-white/25"
                  aria-hidden
                />
                <div
                  ref={progressFillRef}
                  className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-[#ed1c25]"
                />
                <span
                  ref={progressDotRef}
                  className="absolute left-1/2 top-0 z-[1] size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ed1c25]"
                />
              </div>
            </div>

            <div ref={budgetFlowContentRef} className="space-y-5 sm:space-y-6 lg:space-y-8">
              {budgetBlocks.map((block) => (
                <article
                  key={block.title}
                  className="overflow-hidden rounded-2xl bg-white lg:h-[430px]"
                >
                  <div className="grid h-full grid-cols-1 items-center lg:grid-cols-[1fr_0.92fr]">
                    <div className="px-5 py-6 sm:px-6 sm:py-7 lg:px-7 lg:py-7">
                      <h3 className="text-2xl font-semibold leading-tight text-[#111827] sm:text-3xl lg:text-4xl">
                        {block.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-[#4b5563] md:text-base">
                        {block.description}
                      </p>

                      {block.accordionItems.length > 0 ? (
                        <div className="mt-5">
                          <BudgetAccordion items={block.accordionItems} />
                        </div>
                      ) : null}
                    </div>

                    <div className="relative min-h-[220px] sm:min-h-[260px] lg:h-full">
                      <NextImage
                        src={block.image}
                        alt={block.title}
                        fill
                        sizes="(max-width: 768px) 100vw, 45vw"
                        className="object-cover object-center"
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BudgetChecklistSection checklist={checklist} hidden={Boolean(cmsData && !checklist)} />

      <BudgetToolkitSection
        toolkit={toolkit}
        toolkitButtons={toolkitButtons}
        hidden={Boolean(cmsData && !toolkit)}
        locale={locale}
        resolveButtonHref={resolveButtonHref}
      />

      {/* FAQ + Enquiry Form — streamed via Suspense from page.tsx so the
          lead-forms fetch doesn't block above-the-fold paint. */}
      {faqSlot}
    </>
  );
}
